import { useEffect, useMemo, useState } from 'react'
import { Button, EntityIcon, RelationshipTabStrip } from '@solum/ui'
import type { RelationSlot } from './relations'
import type { RelatedCount } from './useRelations'
import { groupSlots, useRelatedOrder } from './useRelatedOrder'
import { useSlotFrequency } from './useSlotFrequency'
import { useEntityIdentities } from '../data/queries'

export interface RelationshipTabsProps {
  entity: string
  slots: RelationSlot[]
  bySlot: Map<string, RelatedCount>
  selected?: RelationSlot
  onSelect: (slot: RelationSlot) => void
  testId?: string
}

/**
 * One relationship navigator for record pages and nested collection workspaces.
 *
 * Its sections and order come from the resolved `core.related_order` rung. A
 * missing count remains unknown, a measured zero is quiet until explicitly
 * revealed, and every tab changes the collection beneath it rather than opening
 * another page. This is intentionally separate from the #2351 Add workspace:
 * reading a relationship and authoring one are different jobs.
 *
 * The DRAWING is the kit's `RelationshipTabStrip` (#2375 §9): bracketed
 * clusters per governed group, a count and a drawn direction arrow on every
 * tab, the active tab joined to the nested card. This file binds the governed
 * order, the frequency ranking and the far entity's registered icon to it, and
 * paints nothing — its private stylesheet is deleted.
 */
export function RelationshipTabs({
  entity, slots, bySlot, selected, onSelect, testId = 'relationship-tabs',
}: RelationshipTabsProps) {
  const order = useRelatedOrder(entity)
  const frequency = useSlotFrequency(entity)
  const identities = useEntityIdentities()
  const iconByEntity = useMemo(
    () => new Map((identities.data ?? []).map((identity) => [identity.entity_type, identity.icon_key])),
    [identities.data],
  )
  const [showEmpty, setShowEmpty] = useState(false)

  const grouped = useMemo(() => groupSlots(
    slots,
    order.rung,
    (slotId) => bySlot.get(slotId)?.n ?? (bySlot.has(slotId) ? 0 : null),
    frequency.countOf,
    order.rung.statedOf,
  ), [slots, order.rung, bySlot, frequency.countOf])

  const ordered = useMemo(() => grouped.flatMap((group) => group.slots), [grouped])
  const empty = useMemo(
    () => ordered.filter((slot) => bySlot.has(slot.id) && (bySlot.get(slot.id)?.n ?? 0) === 0),
    [ordered, bySlot],
  )
  const visible = useMemo(
    () => ordered.filter((slot) => showEmpty || !empty.some((candidate) => candidate.id === slot.id)),
    [ordered, empty, showEmpty],
  )

  useEffect(() => {
    if (visible.length === 0) return
    if (!selected || !visible.some((slot) => slot.id === selected.id)) onSelect(visible[0]!)
  }, [visible, selected, onSelect])

  const bySlotId = useMemo(() => new Map(ordered.map((slot) => [slot.id, slot])), [ordered])
  const groups = grouped.map((group) => ({
    key: group.label,
    label: grouped.length > 1 ? group.label : '',
    tabs: group.slots
      .filter((slot) => showEmpty || !empty.some((candidate) => candidate.id === slot.id))
      .map((slot) => {
        const count = bySlot.get(slot.id)
        return {
          key: slot.id,
          label: slot.label,
          count: count?.n ?? null,
          capped: count?.capped,
          direction: slot.direction,
          icon: <EntityIcon iconKey={iconByEntity.get(slot.otherEntity) ?? 'record'} size={15} />,
        }
      }),
  }))

  return (
    <RelationshipTabStrip testId={testId} groups={groups} selected={selected?.id ?? null}
      tabTestId={(key) => `${testId}-${key}`}
      onSelect={(key) => {
        const slot = bySlotId.get(key)
        if (!slot) return
        frequency.bump(slot.id)
        onSelect(slot)
      }}
      trailing={empty.length > 0 ? (
        <Button type="button" variant="ghost" size="sm" aria-pressed={showEmpty}
          data-testid={`${testId}-empty`} onClick={() => setShowEmpty((value) => !value)}>
          {showEmpty ? 'Hide empty' : `Show ${empty.length} empty`}
        </Button>
      ) : undefined} />
  )
}
